import React, { useState, useEffect, useRef } from "react";

import { NormalizedPrediction } from "../types";
import { ConfidenceBadge, getConfidenceTier, TIER_COLORS } from "../../../shared/components/ConfidenceBadge";
import { ConfidenceExplainer } from "./ConfidenceExplainer";
import { CountdownTimer } from "./CountdownTimer";
import { LineupTable } from "./LineupTable";
import { PitcherCard } from "./PitcherCard";
import { formatGameDateTimeEt, formatGameLocalTime } from "../../../shared/utils/formatters";

interface GameCardProps {
  prediction: NormalizedPrediction;
  /** Whether this game is the highest-confidence pick of the day. */
  isTopPick?: boolean;
}

type DetailPanel = "lineups" | "pitchers" | null;

type GameDisplayStatus = "upcoming" | "live" | "final" | "postponed";

function resolveGameDisplayStatus(prediction: NormalizedPrediction): GameDisplayStatus {
  const status = (prediction.gameStatus ?? "").toLowerCase();
  const detailed = (prediction.statusDetailed ?? "").toLowerCase();

  if (status === "final" || detailed === "final" || detailed === "game over" || detailed === "completed early") {
    return "final";
  }
  if (detailed === "postponed" || detailed === "cancelled" || detailed === "suspended") {
    return "postponed";
  }
  if (status === "live" || detailed === "in progress" || detailed === "warmup") {
    return "live";
  }
  return "upcoming";
}

/**
 * Primary game card component — displays all required game data:
 * - Predicted winner (clearly labeled)
 * - Confidence score from final_confidence (with explainer interaction)
 * - Game date and start time in ET, plus local venue time when it differs
 * - Lineup confirmation status
 * - Starting lineups with season-to-date batting stats
 * - Starting pitchers with season-to-date pitching stats
 */
export function GameCard({ prediction, isTopPick = false }: GameCardProps): JSX.Element {
  const [activePanel, setActivePanel] = useState<DetailPanel>(null);
  const [explainerOpen, setExplainerOpen] = useState(false);
  const explainerWrapRef = useRef<HTMLDivElement>(null);

  // Close explainer when clicking anywhere outside it
  useEffect(() => {
    if (!explainerOpen) return;
    const handleClick = (e: MouseEvent): void => {
      if (explainerWrapRef.current && !explainerWrapRef.current.contains(e.target as Node)) {
        setExplainerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [explainerOpen]);

  const togglePanel = (panel: DetailPanel): void => {
    setActivePanel((current) => (current === panel ? null : panel));
  };

  const isWinnerHome = prediction.predictedWinner === prediction.homeTeam;
  const isWinnerAway = prediction.predictedWinner === prediction.awayTeam;
  const gameDisplayStatus = resolveGameDisplayStatus(prediction);
  // Derive tier color for winner labels so they match the gauge ring
  const tier = getConfidenceTier(prediction.finalConfidence ?? null);
  const [tierColor] = TIER_COLORS[tier] || TIER_COLORS.unavailable;
  const tierBg = `${tierColor}1A`; // ~10% opacity hex
  const tierBorder = `${tierColor}4D`; // ~30% opacity hex
  const localGameTime = formatGameLocalTime(prediction.gameStartTimeUtc, prediction.venueTimeZone);
  const isFinal = gameDisplayStatus === "final";
  const isLive = gameDisplayStatus === "live";
  const isPostponed = gameDisplayStatus === "postponed";

  return (
    <article className={`game-card${isTopPick ? " game-card-top-pick" : ""}${explainerOpen ? " game-card--explainer-open" : ""}${isFinal ? " game-card--final" : ""}${isLive ? " game-card--live" : ""}${isPostponed ? " game-card--postponed" : ""}`}>
      {/* Top Pick badge */}
      {isTopPick && (
        <div className="top-pick-badge" aria-label="Top pick of the day">
          <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor" aria-hidden="true">
            <path d="M8 0l2.35 4.76 5.25.77-3.8 3.7.9 5.24L8 12.17l-4.7 2.3.9-5.24-3.8-3.7 5.25-.77z"/>
          </svg>
          <span>Top Pick</span>
        </div>
      )}

      {/* Game status banner — Live / Final / Postponed */}
      {isLive && (
        <div className="game-status-banner game-status-live" aria-label="Game is live">
          <span className="game-status-dot" aria-hidden="true" />
          <span>Live</span>
        </div>
      )}
      {isFinal && (
        <div className="game-status-banner game-status-final" aria-label="Game is final">
          <span>Final</span>
        </div>
      )}
      {isPostponed && (
        <div className="game-status-banner game-status-postponed" aria-label={`Game ${prediction.statusDetailed ?? "postponed"}`}>
          <span>{prediction.statusDetailed ?? "Postponed"}</span>
        </div>
      )}
      {/* Header: matchup + date/time */}
      <header className="game-card-header">
        <div className="game-card-matchup">
          <h3 className="game-card-teams">
            <span
              className={`team-name ${isWinnerAway ? "team-predicted-winner" : ""}`}
              style={isWinnerAway ? { color: tierColor } : undefined}
            >
              {prediction.awayTeam}
            </span>
            <span className="matchup-at"> @ </span>
            <span
              className={`team-name ${isWinnerHome ? "team-predicted-winner" : ""}`}
              style={isWinnerHome ? { color: tierColor } : undefined}
            >
              {prediction.homeTeam}
            </span>
          </h3>
          <time className="game-card-datetime">
            {formatGameDateTimeEt(prediction.gameDate, prediction.gameStartTimeUtc)}
          </time>
          {localGameTime && <div className="game-card-local-time">{localGameTime}</div>}
          {gameDisplayStatus === "upcoming" && (
            <CountdownTimer
              gameStartTimeUtc={prediction.gameStartTimeUtc}
              gameStatus={prediction.gameStatus}
              statusDetailed={prediction.statusDetailed}
            />
          )}
        </div>

        <div className="game-card-confidence-wrap" ref={explainerWrapRef}>
          <ConfidenceBadge
            confidence={prediction.finalConfidence}
            onClick={() => setExplainerOpen((open) => !open)}
            expanded={explainerOpen}
          />
          <ConfidenceExplainer
            open={explainerOpen}
            onClose={() => setExplainerOpen(false)}
            confidence={prediction.finalConfidence}
          />
        </div>
      </header>

      {/* Predicted winner callout */}
      <div className="game-card-winner" style={{ background: tierBg, borderColor: tierBorder }}>
        <span className="winner-label">Predicted Winner</span>
        <span className="winner-name" style={{ color: tierColor }}>
          {prediction.predictedWinner ?? "Unavailable"}
        </span>
      </div>

      {/* Final score + prediction outcome */}
      {isFinal && prediction.actualHomeScore != null && prediction.actualAwayScore != null && (
        <div className={`game-card-outcome ${prediction.predictionCorrect === true ? "outcome-correct" : prediction.predictionCorrect === false ? "outcome-incorrect" : ""}`}>
          <div className="outcome-score">
            <span className="outcome-score-team">{prediction.awayTeam}</span>
            <span className="outcome-score-value">{prediction.actualAwayScore}</span>
            <span className="outcome-score-separator">-</span>
            <span className="outcome-score-value">{prediction.actualHomeScore}</span>
            <span className="outcome-score-team">{prediction.homeTeam}</span>
          </div>
          {prediction.predictionCorrect === true && (
            <span className="outcome-badge outcome-badge-correct" aria-label="Prediction was correct">
              <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">
                <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"/>
              </svg>
              Correct
            </span>
          )}
          {prediction.predictionCorrect === false && (
            <span className="outcome-badge outcome-badge-incorrect" aria-label="Prediction was incorrect">
              <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">
                <path d="M3.72 3.72a.75.75 0 011.06 0L8 6.94l3.22-3.22a.75.75 0 111.06 1.06L9.06 8l3.22 3.22a.75.75 0 11-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 01-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 010-1.06z"/>
              </svg>
              Incorrect
            </span>
          )}
        </div>
      )}

      {/* Status chips: lineup confirmation + starters */}
      <div className="game-card-status" aria-label="Game status indicators">
        <span
          className={`status-chip ${prediction.lineupConfirmed === true ? "chip-confirmed" : "chip-pending"}`}
          role="img"
          aria-label={
            prediction.lineupConfirmed === null
              ? "Lineup status unknown"
              : prediction.lineupConfirmed
                ? "Lineups confirmed"
                : "Lineups unconfirmed"
          }
        >
          <span className="chip-dot" aria-hidden="true" />
          {prediction.lineupConfirmed === null
            ? "Lineups Unknown"
            : prediction.lineupConfirmed
              ? "Lineups Confirmed"
              : "Lineups Unconfirmed"}
        </span>

        <span
          className={`status-chip ${
            prediction.homeStartingPitcher && prediction.awayStartingPitcher
              ? "chip-confirmed"
              : "chip-pending"
          }`}
        >
          <span className="chip-dot" aria-hidden="true" />
          {prediction.homeStartingPitcher && prediction.awayStartingPitcher
            ? "Starters Announced"
            : "Starters Pending"}
        </span>

      </div>

      {/* Toggle buttons for detail panels */}
      <div className="game-card-toggles">
        <button
          type="button"
          className={`toggle-btn ${activePanel === "pitchers" ? "toggle-active" : ""}`}
          onClick={() => togglePanel("pitchers")}
          aria-expanded={activePanel === "pitchers"}
        >
          Starting Pitchers
        </button>
        <button
          type="button"
          className={`toggle-btn ${activePanel === "lineups" ? "toggle-active" : ""}`}
          onClick={() => togglePanel("lineups")}
          aria-expanded={activePanel === "lineups"}
        >
          Starting Lineups
        </button>
      </div>

      {/* Detail panel: pitchers */}
      {activePanel === "pitchers" && (
        <div className="game-card-detail-panel" aria-label="Starting pitchers">
          <div className="pitchers-grid">
            <PitcherCard teamName={prediction.awayTeam} pitcher={prediction.awayStartingPitcher} />
            <PitcherCard teamName={prediction.homeTeam} pitcher={prediction.homeStartingPitcher} />
          </div>
        </div>
      )}

      {/* Detail panel: lineups */}
      {activePanel === "lineups" && (
        <div className="game-card-detail-panel" aria-label="Starting lineups">
          <LineupTable teamName={prediction.awayTeam} lineup={prediction.awayStartingLineup} />
          <LineupTable teamName={prediction.homeTeam} lineup={prediction.homeStartingLineup} />
        </div>
      )}
    </article>
  );
}
